import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Machine } from '../types/machine';
import { IApiBackendRouteService } from '../abstract/api-routes.service';
import { API_ENDPOINTS } from '../../../core/providers/constants/api.constants';
import { MachineApiRoutesDto } from '../dto/machine.dto';
import { ValidMachine } from 'src/app/views/scheduler-stage/interfaces/valid-machine.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiRouteMachinesService extends IApiBackendRouteService {
  constructor(protected readonly http: HttpClient) {
    super();
  }

  urlRoute = `${API_ENDPOINTS.MACHINES}`;

  getByType(typeId: number): Observable<Machine[]> {
    return this.http.get<Machine[]>(`${this.urlRoute}/get-by-type/${typeId}`);
  }

  getAll(query: {
    departmentName?: string;
    typeId?: number;
  }): Observable<Machine[]> {
    const { departmentName, typeId } = query;
    let params = new HttpParams();
    if (departmentName) {
      params = params.set('departmentName', departmentName);
    }
    if (typeId) {
      params = params.set('typeId', typeId);
    }
    return this.http.get<Machine[]>(`${this.urlRoute}?${params.toString()}`);
  }

  add(data: MachineApiRoutesDto['add']): Observable<Machine> {
    return this.http.post<Machine>(`${this.urlRoute}`, data);
  }

  update(id: number, data: MachineApiRoutesDto['update']): Observable<Machine> {
    return this.http.patch<Machine>(`${this.urlRoute}/${id}`, data);
  }

  replaceOperator(body: {
    machineId: number;
    operatorId: number;
    department: string;
    numberOfReplacements?: number;
  }): Observable<boolean> {
    return this.http.post<boolean>(`${this.urlRoute}/operator/replace`, body);
  }

  getRotoMoldMachines(): Observable<Machine[]> {
    return this.http.get<Machine[]>(`${this.urlRoute}/rotomolding`);
  }

  createMachineWorkOrder(
    body: MachineApiRoutesDto['createMachineWorkOrder']['body']
  ): Observable<any> {
    return this.http.post(`${this.urlRoute}/planning-wo`, {
      ...body,
    });
  }

  startPlanningOnMachine(taskId: number, machineId: number): Observable<any> {
    return this.http.patch(`${this.urlRoute}/planning-wo/start`, {
      machineId,
      taskId,
    });
  }

  removeFinishedTasksFromMachine(): Observable<any> {
    return this.http.patch(`${this.urlRoute}/planning-wo/remove-finished`, {});
  }

  setPlanningAsNotRunning(
    machineId: number,
    taskId: number,
    isCurrent?: boolean
  ): Observable<boolean> {
    return this.http.patch<boolean>(
      `${this.urlRoute}/planning-wo/not-running`,
      {
        machineId,
        taskId,
        isCurrent,
      }
    );
  }

  deletePlanningFromMachine(
    taskId: number,
    machineId: number
  ): Observable<any> {
    return this.http.delete(`${this.urlRoute}/planning-wo/remove`, {
      body: {
        machineId,
        taskId,
      },
    });
  }

  setPlanningToCurrent(taskId: number, machineId: number): Observable<any> {
    return this.http.patch(`${this.urlRoute}/planning-wo/to-current`, {
      machineId,
      taskId,
    });
  }

  removeAllPlanningFromMachines(taskId: number): Observable<any> {
    return this.http.delete(`${this.urlRoute}/planning-wo/remove-item`, {
      body: { taskId },
    });
  }

  getMoldRelations(departmentName: string): Observable<Machine[]> {
    return this.http.get<Machine[]>(`${this.urlRoute}/relations-to/molds`, {
      params: {
        departmentName,
      },
    });
  }

  getValidMachines(transactionNumber: string, userId?: number): Observable<ValidMachine[]> {
    let params = new HttpParams();

    if (userId) {
      params = params.set('userId', userId.toString());
    }
    return this.http.get<ValidMachine[]>(`${this.urlRoute}/valid-machines/${transactionNumber}`, { params });
  }
}
